/**
 * TellerConnect — Bank account linking widget wrapper
 */

import React, { useEffect, useRef, useState } from 'react';
import { callSaveTellerEnrollment } from '../services/functions';

export default function TellerConnect({ onEnrollmentAdded }) {
  const tellerRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const appId = process.env.REACT_APP_TELLER_APP_ID;

  useEffect(() => {
    if (!appId) return;

    // Dynamically inject SDK so service worker cache doesn't block it
    if (!document.querySelector('script[src*="cdn.teller.io"]')) {
      const script = document.createElement('script');
      script.src = 'https://cdn.teller.io/connect/connect.js';
      document.head.appendChild(script);
    }

    const setup = () => {
      if (!window.TellerConnect) return false;
      const tellerEnv = process.env.REACT_APP_TELLER_ENV || 'sandbox';
      console.log('[TellerConnect] env:', tellerEnv, 'appId:', appId);
      tellerRef.current = window.TellerConnect.setup({
        applicationId: appId,
        environment: tellerEnv,
        onInit: () => {},
        onSuccess: async (enrollment) => {
          setLoading(true);
          setError(null);
          try {
            await callSaveTellerEnrollment({
              enrollmentId: enrollment.enrollment.id,
              accessToken: enrollment.accessToken,
              institutionName: enrollment.enrollment.institution.name,
              institutionId: enrollment.enrollment.institution.id,
            });
            onEnrollmentAdded?.();
          } catch (err) {
            setError('Failed to save enrollment: ' + err.message);
            console.error('[TellerConnect]', err);
          } finally {
            setLoading(false);
          }
        },
        onExit: () => setLoading(false),
      });
      setReady(true);
      return true;
    };

    // Try immediately, then poll every 200ms until SDK is available
    if (!setup()) {
      const interval = setInterval(() => {
        if (setup()) clearInterval(interval);
      }, 200);
      return () => clearInterval(interval);
    }
  }, [appId, onEnrollmentAdded]);

  const handleOpen = () => {
    if (tellerRef.current) {
      tellerRef.current.open();
    } else {
      setError('Bank connection not ready. Please refresh the page.');
    }
  };

  if (!appId) {
    return (
      <p style={{ fontSize: '0.75rem', color: '#999' }}>
        Teller not configured — set REACT_APP_TELLER_APP_ID in .env
      </p>
    );
  }

  return (
    <div>
      <button
        className="btn btn-primary btn-sm"
        onClick={handleOpen}
        disabled={loading || !ready}
      >
        {loading ? 'Connecting...' : !ready ? 'Loading...' : '+ Connect Bank Account'}
      </button>
      {error && (
        <p style={{ color: '#c0392b', fontSize: '0.8rem', marginTop: '0.4rem' }}>
          {error}
        </p>
      )}
    </div>
  );
}
